import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Package, Search } from 'lucide-react';
import { ProductCard } from '@/components/catalog/ProductCard';
import { api } from '@/lib/api';
import { MOCK_PRODUCTOS } from '@/data/mockCatalog';
import { CATEGORIAS_DEFAULT } from '@/types';
import type { Producto } from '@/types';
import { CatalogBanner } from '@/components/layout/CatalogBanner';
import { Chip, EmptyState, SkeletonGrid } from '@/components/ui';

export function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoria = searchParams.get('categoria') ?? '';
  const [q, setQ] = useState(searchParams.get('q') ?? '');
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .productos({ categoria: categoria || undefined, q: q || undefined })
      .then((res) => setProductos(res.items))
      .catch(() => setProductos(MOCK_PRODUCTOS))
      .finally(() => setLoading(false));
  }, [categoria, q]);

  const filtrarCategoria = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug) next.set('categoria', slug);
    else next.delete('categoria');
    setSearchParams(next);
  };

  const catLabel =
    CATEGORIAS_DEFAULT.find((c) => c.slug === categoria)?.nombre ?? null;

  return (
    <>
      <CatalogBanner
        title={catLabel ?? 'Catálogo'}
        subtitle="Existencia del almacén central. Filtra por línea o busca por modelo."
        breadcrumb={[
          { label: 'Catálogo', to: catLabel ? '/catalogo' : undefined },
          ...(catLabel ? [{ label: catLabel }] : []),
        ]}
      />

      <div className="page-section">
        <div className="container-app">
          <div className="grid gap-8 lg:grid-cols-[220px_1fr] xl:grid-cols-[240px_1fr]">
            {/* Sidebar filtros — desktop */}
            <aside className="hidden lg:block">
              <div className="panel sticky top-20 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-surface-500">
                  Categoría
                </p>
                <div className="mt-3 flex flex-col gap-1">
                  <Chip active={!categoria} onClick={() => filtrarCategoria('')}>
                    Todas
                  </Chip>
                  {CATEGORIAS_DEFAULT.map((cat) => (
                    <Chip
                      key={cat.slug}
                      active={categoria === cat.slug}
                      onClick={() => filtrarCategoria(cat.slug)}
                      className="justify-start capitalize"
                    >
                      {cat.nombre}
                    </Chip>
                  ))}
                </div>
                <p className="mt-6 border-t border-surface-200 pt-4 text-xs text-surface-500">
                  {loading ? '…' : `${productos.length} resultados`}
                </p>
              </div>
            </aside>

            <div>
              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
                  <input
                    type="search"
                    placeholder="Modelo, nombre o SKU…"
                    className="input-field-lg pl-10"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
                <p className="text-sm text-surface-500 lg:hidden">
                  {loading ? 'Cargando…' : `${productos.length} productos`}
                </p>
              </div>

              {/* Chips móvil — scroll horizontal */}
              <div className="mb-5 flex gap-2 overflow-x-auto pb-1 lg:hidden">
                <Chip active={!categoria} onClick={() => filtrarCategoria('')}>
                  Todos
                </Chip>
                {CATEGORIAS_DEFAULT.map((cat) => (
                  <Chip
                    key={cat.slug}
                    active={categoria === cat.slug}
                    onClick={() => filtrarCategoria(cat.slug)}
                    className="shrink-0 capitalize"
                  >
                    {cat.nombre}
                  </Chip>
                ))}
              </div>

              {loading ? (
                <SkeletonGrid />
              ) : productos.length === 0 ? (
                <EmptyState
                  icon={<Package className="h-6 w-6" />}
                  title="Sin resultados"
                  description="Prueba otra categoría o término de búsqueda."
                />
              ) : (
                <div className="grid gap-3 grid-cols-2 xl:grid-cols-3">
                  {productos.map((p) => (
                    <ProductCard key={p.id_pto} product={p} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
